//#region types
import { AuthEnums, FileEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { DocumentFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region components
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
import { FileHelper } from "utils/helpers";

function useDocumentService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getSharedDocuments = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DocumentClient.GetSharedDocuments()
      .then((res) => res.data.sort((a, b) => a.type - b.type));
  };
  const getSharedDocumentsByParent = (parentId: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DocumentClient.GetSharedDocumentsByParent(parentId)
      .then((res) => res.data.sort((a, b) => a.type - b.type));
  };
  const downloadSharedDocument = (id: string, type?: ApiModels.ExportDocumentType) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).DocumentClient.DownloadSharedDocument(id, type);
  };

  const getSIEFiles = async (pagination: WintUtilsWebApi.Filter, filter: DocumentFormTypes.SIEFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).DocumentClient.FilterSIEFiles({
      ...pagination,
      ...reportToAPIFilterSIE(filter),
    });
  };

  const downloadSIEFile = async (id: number, actionType: ApiModels.SIEFileActionType) => {
    const pdfResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).DocumentClient.DownloadSIEFile(id, actionType);
    FileHelper.download(pdfResult);
  };

  const uploadSIEFile = (file: ApiModels.SIEFilePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DocumentClient.UploadSIEFiles(file);
  };

  const uploadSelfInvoice = (file: ApiModels.SelfInvoiceImageDataPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).SelfInvoiceClient.Post(file);
  };

  const reportToAPIFilterSIE = (
    formFilter?: DocumentFormTypes.SIEFilter
  ): Omit<ApiModels.SIEFileFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      fileName: formFilter?.fileName,
      uploadedByIds: formFilter?.uploadedById ? [formFilter?.uploadedById] : undefined,
      uploadedFrom: formFilter?.uploadedDate?.start,
      uploadedTo: formFilter?.uploadedDate?.end,
    };
  };

  const formToSIEFilePost = (values: DocumentFormTypes.CreateNew): ApiModels.SIEFilePost => {
    if (!values.filesToUpload) return {};
    const file = values.filesToUpload[0];
    return {
      data: file.data,
      contentType: FileEnums.ContentTypes.Text,
      name: file.fileName,
      comment: values.document.subject,
    };
  };

  const formToSelfInvoiceFilePost = (values: DocumentFormTypes.CreateNew): ApiModels.SelfInvoiceImageDataPost => {
    if (!values.filesToUpload) return {};
    const file = values.filesToUpload[0];
    return {
      content: file.data,
      contentType: FileEnums.ContentTypes.ApplicationPDF,
      fileName: file.fileName,
    };
  };

  const exportSIEFilePost = async (post: ApiModels.ExportSIEFilePost) => {
    const pdfResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).DocumentClient.ExportSIEFile(post);
    FileHelper.download(pdfResult);
    return Promise.resolve();
  };

  const queueExportSIEFile = async (post: ApiModels.ExportSIEFilePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company).DocumentClient.QueueExportSIEFile(post);
  };

  const convertToSharedDocumentPost = (files?: UploadedFile[]) => {
    const supportCommentPost: ApiModels.SharedDocumentPost = {
      filesToUpload: files
        ? files?.map(
            (file) =>
              ({
                data: file.data,
                // Browsers report SIE files with an empty content type; send a generic one to Google Drive instead.
                contentType: file.contentType || FileEnums.ContentTypes.ApplicationOctetStream,
                name: file.fileName,
              }) as ApiModels.SharedDocumentToUpload
          )
        : [],
    };

    return supportCommentPost;
  };

  const uploadAdvisorDocuments = async (post: ApiModels.SharedDocumentPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).DocumentClient.UploadAdvisorDocuments(
      post
    );
  };

  const exportSharedFolderForFinancialYear = async (financialYearId: number) => {
    const exportResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).DocumentClient.ExportSharedFolderForFinancialYear(financialYearId);
    FileHelper.download(exportResult);
  };

  const getSharedFolderIdForFinancialYear = async (financialYearId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).DocumentClient.GetSharedFolderIdForFinancialYear(
      financialYearId
    );
  };

  const getSharedDocumentsForFinancialYear = async (financialYearId: number) => {
    const { data: sharedFolderId } = await getSharedFolderIdForFinancialYear(financialYearId);
    return sharedFolderId ? getSharedDocumentsByParent(sharedFolderId) : [];
  };

  return {
    getSharedDocuments,
    getSharedDocumentsByParent,
    downloadSharedDocument,
    getSIEFiles,
    downloadSIEFile,
    uploadSIEFile,
    uploadSelfInvoice,
    reportToAPIFilterSIE,
    formToSIEFilePost,
    formToSelfInvoiceFilePost,
    exportSIEFilePost,
    convertToSharedDocumentPost,
    uploadAdvisorDocuments,
    exportSharedFolderForFinancialYear,
    queueExportSIEFile,
    getSharedFolderIdForFinancialYear,
    getSharedDocumentsForFinancialYear,
  };
}

export default useDocumentService;
